import React, { useState, useEffect } from 'react';
import PageTitle from '../../components/PageTitle';
import { AuspiceDto } from '../../common/Types';
import { institutionService } from '../../services/InstitutionService';
import { toast } from 'react-toastify';
import { Row, Col, Card, CardBody, FormGroup, Form } from 'reactstrap';
import * as yup from 'yup';
import { Input, Button } from '../../components';
import { useForm, useFieldArray, Controller } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';

interface Auspices {
    auspices: AuspiceDto[];
    deletedAuspices: AuspiceDto[];
}

const schema = yup
    .object({
        auspiceID: yup.number().required().default(0),
        title: yup.string().required(),
        isActive: yup.boolean().required().default(true),
    })
    .defined();

const listSchema = yup.object().shape({
    auspices: yup.array().of(schema),
    deletedAuspices: yup.array().of(schema),
});

const Auspice = () => {
    const [auspices, setAuspices] = useState<AuspiceDto[]>([]);
    const [deletedAuspices, setDeletedAuspices] = useState<AuspiceDto[]>([]);
    const [editing, setEditing] = useState(false);

    const { control, handleSubmit, reset, formState: { errors } } = useForm({
        resolver: yupResolver(listSchema as any),
        defaultValues: { auspices: [], deletedAuspices: [] } as Auspices,
    });

    const { fields, append, remove } = useFieldArray({
        control,
        name: 'auspices',
    });

    const {
        fields: deletedFields,
        append: deletedAppend,
        remove: deletedRemove,
    } = useFieldArray({
        control,
        name: 'deletedAuspices',
    });

    const deleteAuspice = (aus, i) => {
        remove(i);
        aus.isActive = false;
        deletedAppend(aus);
    };

    const onSubmit = (formData: Auspices) => {
        const toastId = toast.info('Saving Auspices...');

        // Add back deleted auspices
        const saveObj = [...formData.auspices, ...(formData.deletedAuspices ?? [])];

        institutionService
            .saveAuspice(saveObj)
            .then((response) => {
                toast.update(toastId, {
                    type: 'success',
                    render: 'Auspices saved successfuly',
                });
                reset({
                    auspices: response.filter((x) => x.isActive),
                    deletedAuspices: response.filter((x) => !x.isActive),
                });
                setAuspices(response.filter((x) => x.isActive));
                setDeletedAuspices(response.filter((x) => !x.isActive));
                setEditing(false);
            })
            .catch(() =>
                toast.update(toastId, {
                    type: 'error',
                    render: 'Error saving Auspices',
                }),
            );
    };

    useEffect(() => {
        const getData = () => {
            institutionService
                .getAuspice()
                .then((response) => {
                    setAuspices(response.filter((x) => x.isActive));
                    setDeletedAuspices(response.filter((x) => !x.isActive));
                    reset({
                        auspices: response.filter((x) => x.isActive),
                        deletedAuspices: response.filter((x) => !x.isActive),
                    });
                })
                .catch(() => {
                    toast.error('Failed to get Auspices.');
                });
        };

        getData();
    }, [setAuspices, setDeletedAuspices, reset]);

    return (
        <>
            <PageTitle title="Auspices" />
            <Row>
                <Col>
                    <Card>
                        <CardBody>
                            {!editing ? (
                                <div>
                                    <h3 className="card-title">Active Auspices</h3>
                                    {auspices.map((aus, i) => (
                                        <div key={i}>
                                            <p>{aus.title}</p>
                                        </div>
                                    ))}
                                    <h3 className="card-title">Deleted Auspices</h3>
                                    {deletedAuspices.map((aus, i) => (
                                        <div key={i}>
                                            <p>{aus.title}</p>
                                        </div>
                                    ))}
                                    <Button color="primary" onClick={() => setEditing(true)}>
                                        Edit Auspices
                                    </Button>
                                </div>
                            ) : (
                                <Form noValidate onSubmit={handleSubmit(onSubmit)}>
                                    <h3 className="card-title">Active Auspices</h3>
                                    {fields.map((aus, i) => (
                                        <div key={aus.id} style={{ display: 'flex' }}>
                                            <FormGroup>
                                                <Controller
                                                    name={`auspices.${i}.title`}
                                                    control={control}
                                                    render={({ field }) => (
                                                        <Input
                                                            type="text"
                                                            {...field}
                                                            value={field.value ?? ''}
                                                            style={{ minWidth: '400px' }}
                                                        />
                                                    )}
                                                />
                                                {(errors?.auspices as any)?.[i]?.title?.message && (
                                                    <span className={'text-danger'}>{(errors.auspices as any)[i].title.message}</span>
                                                )}
                                                <Controller
                                                    name={`auspices.${i}.auspiceID`}
                                                    control={control}
                                                    render={({ field }) => (
                                                        <input type="hidden" {...field} value={field.value ?? ''} />
                                                    )}
                                                />
                                                <Controller
                                                    name={`auspices.${i}.isActive`}
                                                    control={control}
                                                    render={({ field }) => (
                                                        <input
                                                            type="hidden"
                                                            {...field}
                                                            value={field.value ? 'true' : 'false'}
                                                        />
                                                    )}
                                                />
                                            </FormGroup>
                                            &nbsp;
                                            <div>
                                                <Button
                                                    type={'button'}
                                                    color={'danger'}
                                                    onClick={() => deleteAuspice(aus, i)}
                                                >
                                                    <i className={'remixicon-delete-bin-line'}></i>
                                                </Button>
                                            </div>
                                        </div>
                                    ))}
                                    <h3 className="card-title">Deleted Auspices</h3>
                                    {deletedFields.map((aus, i) => (
                                        <div key={aus.id} style={{ display: 'flex' }}>
                                            <FormGroup>
                                                <Controller
                                                    name={`deletedAuspices.${i}.title`}
                                                    control={control}
                                                    render={({ field }) => (
                                                        <Input
                                                            type="text"
                                                            {...field}
                                                            value={field.value ?? ''}
                                                            style={{ minWidth: '400px' }}
                                                        />
                                                    )}
                                                />
                                                <Controller
                                                    name={`deletedAuspices.${i}.auspiceID`}
                                                    control={control}
                                                    render={({ field }) => (
                                                        <input type="hidden" {...field} value={field.value ?? ''} />
                                                    )}
                                                />
                                                <Controller
                                                    name={`deletedAuspices.${i}.isActive`}
                                                    control={control}
                                                    render={({ field }) => (
                                                        <input
                                                            type="hidden"
                                                            {...field}
                                                            value={field.value ? 'true' : 'false'}
                                                        />
                                                    )}
                                                />
                                            </FormGroup>
                                            &nbsp;
                                            <div>
                                                <Button
                                                    type={'button'}
                                                    color={'info'}
                                                    onClick={() => {
                                                        aus.isActive = true;
                                                        append(aus);
                                                        deletedRemove(i);
                                                    }}
                                                >
                                                    <i className={'remixicon-eye-line'}></i>
                                                </Button>
                                            </div>
                                        </div>
                                    ))}

                                    <div className="mb-2">
                                        <Button
                                            color="info"
                                            onClick={() => append({ auspiceID: 0, isActive: true, title: '' })}
                                        >
                                            Add Auspice
                                        </Button>
                                    </div>
                                    <div>
                                        <Button color="primary" type="submit">
                                            Save
                                        </Button>
                                        &nbsp;
                                        <Button outline onClick={() => setEditing(false)}>
                                            Cancel
                                        </Button>
                                    </div>
                                </Form>
                            )}
                        </CardBody>
                    </Card>
                </Col>
            </Row>
        </>
    );
};

export default Auspice;
